import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { removeFromLibrary, saveToLibrary, type Book } from "../api";
import { useT } from "../i18n";
import { useSession } from "../session";
import { LibraryIcon } from "./icons";

/**
 * SaveButton keeps a book, or stops keeping it (ADR-0060).
 *
 * One control with two states rather than two controls: the shelf is a toggle,
 * and a separate Remove somewhere else is a thing to go and find.
 *
 * It is optimistic. The server is the authority on `in_library`, but a phone
 * tap that does nothing for 300ms reads as a tap that missed, and the answer
 * here is never in doubt — a save either happened or the book is not one a
 * library may hold, and that case is rare enough to correct after the fact.
 * A failure puts the state back and says so.
 *
 * Signed-out visitors get nothing: there is nowhere to keep a book without an
 * account, and a button that asks you to sign in before it will do the small
 * thing you asked is worse than no button.
 */
export default function SaveButton({
  book,
  variant = "tile",
}: {
  book: Book;
  variant?: "tile" | "page";
}) {
  const { isSignedIn } = useSession();
  const t = useT();
  const client = useQueryClient();
  const [saved, setSaved] = useState(book.in_library ?? false);
  const [failed, setFailed] = useState(false);

  const toggle = useMutation({
    mutationFn: (next: boolean) => (next ? saveToLibrary(book.id) : removeFromLibrary(book.id)),
    onMutate: (next: boolean) => {
      setFailed(false);
      setSaved(next);
    },
    onError: (_error, next) => {
      setSaved(!next);
      setFailed(true);
    },
    // The shelf and every list carrying in_library are now stale. Both keys,
    // because a save made from the catalogue changes the library and a removal
    // made from the library changes the catalogue's flag.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["library"] });
      void client.invalidateQueries({ queryKey: ["books"] });
    },
  });

  if (!isSignedIn) return null;

  const label = saved ? t.library.remove : t.library.save;

  if (variant === "page") {
    return (
      <div>
        <button
          type="button"
          // Its own id, not the tile's: a book page reached from the
          // catalogue briefly has both DOMs live while the router swaps, and
          // one id for both makes every query on it ambiguous rather than
          // wrong — which is a flaky test, not a failing one.
          data-testid="save-book-page"
          data-saved={saved}
          aria-pressed={saved}
          onClick={() => toggle.mutate(!saved)}
          className={`inline-flex items-center gap-2 rounded-control px-3.5 py-2 text-sm font-medium transition-colors ${
            saved
              ? "bg-sage/15 text-sage-deep hover:bg-sage/25"
              : "bg-sunk text-ink-soft hover:bg-sunk/70 hover:text-ink"
          }`}
        >
          <LibraryIcon className="size-4" added={saved} />
          {saved ? t.library.saved : t.library.save}
        </button>
        {failed && (
          <p role="status" className="mt-1 text-xs text-red-600 dark:text-red-400">
            {t.library.saveFailed}
          </p>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="save-book"
      data-saved={saved}
      aria-pressed={saved}
      aria-label={label}
      title={label}
      onClick={() => toggle.mutate(!saved)}
      // Over the cover's top corner, and outside the tile's own link: a button
      // inside an anchor is not a button, and tapping it would open the book.
      // Kept is a solid sage disc, not kept a frosted one: the state reads
      // from the colour before the shape inside it.
      className={`absolute end-1.5 top-1.5 z-10 grid size-9 place-items-center rounded-full shadow-sm backdrop-blur-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
        saved ? "bg-sage text-on-sage hover:bg-sage-deep" : "bg-ground/75 text-ink hover:bg-ground"
      }`}
    >
      <LibraryIcon className="size-[18px]" added={saved} />
    </button>
  );
}
